import {
  NetworkStatus,
  type TypedDocumentNode,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  type FacetedFilterOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusUI,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { RiBuilding2Line, RiFilter3Line, RiPriceTagLine } from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import {
  statementKindValues,
  statementStatusValues,
} from "~/gql/client/enumValues";
import { graphql } from "~/gql/client/gql";
import type {
  AdminStatementsPageQuery,
  AdminStatementsPageQueryVariables,
  AdminStatementsQuery,
  AdminStatementsQueryVariables,
  StatementFilter,
  StatementKind,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/statements")({
  head: () => opsHead("Statements"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

type StatementRow = NonNullable<
  NonNullable<AdminStatementsQuery["statements"]>["statements"]
>[number];

const columns: ColumnDef<StatementRow>[] = [
  {
    id: "statementId",
    accessorFn: (row) => row.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      return <div>{row.original.id}</div>;
    },
    enableSorting: false,
  },
  {
    id: "organisationId",
    accessorFn: (row) => row.organisation?.id,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => {
      return (
        <div>
          {row.original.organisation?.name ?? row.original.organisation?.id}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "kind",
    accessorFn: (row) => row.__typename,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      return (
        <div>{prettyPrintEnum(typenameToKind(row.original.__typename))}</div>
      );
    },
    enableSorting: false,
  },
  {
    id: "period",
    accessorFn: (row) =>
      `${"periodStartAtInclusive" in row ? `${row.periodStartAtInclusive.toISOString()}-` : ""}${row.periodEndAtExclusive.toISOString()}`,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <div className="flex flex-row">
        {"periodStartAtInclusive" in row.original && (
          <>
            <DateWithDelta date={row.original.periodStartAtInclusive} />
            <span>&nbsp;-&nbsp;</span>
          </>
        )}
        <DateWithDelta date={row.original.periodEndAtExclusive} />
      </div>
    ),
    enableSorting: false,
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return row.original.status ? (
        <StatusUI status={row.original.status} />
      ) : (
        "-"
      );
    },
    enableSorting: false,
  },
];

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      organisationIds: z.string().array().nullish(),
      statuses: z.enum(statementStatusValues).array().nullish(),
      kinds: z.enum(statementKindValues).array().nullish(),
    }),
    defaultState: {},
  });

  const baseInput = {
    first: 20,
    filter,
  };

  const {
    fetchMore,
    loading,
    networkStatus,
    data: tableData,
  } = useQuery(
    graphql(`
      query AdminStatements(
        $after: String
        $first: Int!
        $filter: StatementFilter!
      ) {
        statements(after: $after, first: $first, filter: $filter) {
          pageInfo {
            endCursor
            hasNextPage
          }
          statements {
            ...AdminStatementRow
          }
        }
      }

      fragment AdminStatementRow on Statement {
        id
        status
        organisation {
          id
          name
        }
        periodEndAtExclusive
        ... on ActivityStatement {
          periodStartAtInclusive
        }
        ... on MonthlyStatement {
          periodStartAtInclusive
        }
      }
    `) as TypedDocumentNode<
      AdminStatementsQuery,
      AdminStatementsQueryVariables
    >,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminStatementsPage {
        organisations {
          id
          name
        }
      }
    `) as TypedDocumentNode<
      AdminStatementsPageQuery,
      AdminStatementsPageQueryVariables
    >,
    {
      fetchPolicy: "cache-first",
    },
  );

  const organisations = data?.organisations ?? [];

  const statements = tableData?.statements?.statements ?? [];

  const nextCursor = tableData?.statements?.pageInfo.hasNextPage
    ? (tableData.statements.pageInfo.endCursor ?? undefined)
    : undefined;

  const organisationOptions: FacetedFilterOption[] = [...organisations]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      value: o.id,
      searchValues: [o.name],
      label: <div>{o.name}</div>,
    }));

  return (
    <AdminPageContainer testId="admin-statements">
      <AdminPageHeader
        title="Statements"
        subtitle="Generate, review, and publish customer statements."
      />
      <div>
        <DataTable
          data={statements}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          maxMultiSortColCount={1}
          hidePagination={true}
          manualSorting={true}
          pageSize={10_000}
          onRowClick={(row) => {
            navigate({
              to: "/statements/$statementId",
              params: { statementId: row.original.id },
            });
          }}
          className="overflow-hidden border"
          initialColumnVisibility={{
            statementId: false,
          }}
          toolbarComponent={() => (
            <FilterBar
              filterValue={filter}
              organisationOptions={organisationOptions}
              onFilterChange={setFilter}
            />
          )}
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!statements.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

function typenameToKind(s: string): StatementKind {
  switch (s) {
    case "MonthlyStatement":
      return "MONTHLY";
    case "ActivityStatement":
      return "ACTIVITY";
    case "BalanceStatement":
      return "BALANCE";
    case "AccountStatement":
      return "ACCOUNT";
    default:
      throw new Error(`unknown __typename: "${s}"`);
  }
}

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
}: {
  filterValue: StatementFilter;
  onFilterChange: (_: StatementFilter) => void;
  organisationOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("organisation");
  let filterCount = 0;
  if (!!filterValue?.organisationIds?.length) {
    filterCount++;
  }
  if (!!filterValue?.kinds?.length) {
    filterCount++;
  }
  if (!!filterValue?.statuses?.length) {
    filterCount++;
  }

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      statuses: undefined,
      organisationIds: undefined,
      kinds: undefined,
    });
  };

  const statusOptions = statementStatusValues.map((k) => ({
    value: k,
    label: prettyPrintEnum(k),
  }));

  const kindOptions = statementKindValues.map((k) => ({
    value: k,
    label: prettyPrintEnum(k),
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0 " align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisation")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("kind")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Kind</div>
                {(filterValue.kinds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.statuses?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "organisation" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues.filter((v) => v !== null)
                      : undefined,
                  });
                }}
              />
            )}
            {path === "kind" && (
              <ControlledFacetedFilterControl
                options={kindOptions}
                selectedValues={filterValue.kinds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    kinds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.statuses ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    statuses: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisation");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Kind"
        options={kindOptions}
        selectedValues={filterValue.kinds ?? []}
        onClick={() => {
          setPath("kind");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.statuses ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
